/* PreNumbers Critical Finance — Projections (reskinned).

   All DB calls and handlers (add / save / delete year, IPO gating) are
   unchanged from the original — only the markup is reskinned to .pn-*. */

import { useState, useContext, useEffect } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { CriticalFinancePageContext } from '../CriticalFiancePage.jsx';
import { genericPOSTDatabaseCall, genericDELETEDatabaseCall } from '../../helperFunctions.jsx';
import { PageHead, FbIcons } from '../../FinancialBuilder/fbUI.jsx';

const { ArrowLeftOutlined, ArrowRightOutlined, PlusOutlined, SaveOutlined } = FbIcons;

async function addYearToDatabase(info) {
  return genericPOSTDatabaseCall('/api/cfi/addEntityYear', info, 'entity year');
}
async function deleteYearInDatabase(year) {
  return genericDELETEDatabaseCall('/api/cfi/deleteEntityYear/', { year }, 'entity year');
}
async function saveYearsInDatabase(years) {
  return genericPOSTDatabaseCall('/api/cfi/updateEntityYears', { years }, 'entity years');
}

function RenderYears({ years, setCompanySelected, messageApi }) {
  // Group years into chunks of 4
  const chunkedYears = [];
  for (let i = 0; i < years.length; i += 4) {
    chunkedYears.push(years.slice(i, i + 4));
  }

  const handleInputChange = (event, yearIndex, field) => {
    const newValue = event.target.value;
    setCompanySelected(prev => {
      const updatedYears = [...prev.years];
      updatedYears[yearIndex] = { ...updatedYears[yearIndex], [field]: newValue };
      return { ...prev, years: updatedYears };
    });
  };

  const handleInputBlur = (event, yearIndex, field) => {
    var newValue = event.target.value;
    const regex = /^(\d{0,13}(\.\d{0,2})?)?$/;
    if (!regex.test(newValue)) {
      messageApi.error('Invalid input. Please enter a valid number. Max 13 digits before decimal and 2 after.');
      newValue = '0.00';
    }
    if (newValue === '') {
      messageApi.error('This field cannot be empty');
      newValue = '0.00';
    }
    setCompanySelected(prev => {
      const updatedYears = [...prev.years];
      updatedYears[yearIndex] = { ...updatedYears[yearIndex], [field]: newValue };
      return { ...prev, years: updatedYears };
    });
  };

  const handleDeleteYear = async (deleteYear) => {
    messageApi.loading('Deleting year...');
    try {
      await deleteYearInDatabase(deleteYear);
      setCompanySelected(prev => ({ ...prev, years: prev.years.filter(year => year.id !== deleteYear.id) }));
      messageApi.destroy();
      messageApi.success('Year deleted successfully');
    } catch (error) {
      messageApi.destroy();
      messageApi.error('Failed to delete year');
    }
  };

  const cell = (yearData, absoluteIndex, field) => (
    <td key={`${field}-${absoluteIndex}`}>
      <div className="pn-input-wrap has-pre">
        <span className="pre">$</span>
        <input
          type="text"
          className="pn-control"
          value={yearData[field]}
          onChange={(e) => handleInputChange(e, absoluteIndex, field)}
          onBlur={(e) => handleInputBlur(e, absoluteIndex, field)}
        />
      </div>
    </td>
  );

  if (years.length === 0) {
    return <div className="pn-fc-empty">No projection years yet. Add one below.</div>;
  }

  return (
    <div className="pn-table-wrap" style={{ padding: '4px 16px' }}>
      {chunkedYears.map((yearChunk, chunkIndex) => (
        <table key={chunkIndex} className="pn-year-table">
          <thead>
            <tr>
              <th></th>
              {yearChunk.map((yearData, index) => (
                <th key={`year-${index}`}>
                  {"Year " + (index + chunkIndex * 4)}
                  <DeleteOutlined className="delete-year-icon" onClick={() => handleDeleteYear(yearData)} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Operating revenue</td>
              {yearChunk.map((yearData, yearIndex) => cell(yearData, yearIndex + chunkIndex * 4, 'revenue'))}
            </tr>
            <tr>
              <td>Operating expenses</td>
              {yearChunk.map((yearData, yearIndex) => cell(yearData, yearIndex + chunkIndex * 4, 'expenses'))}
            </tr>
          </tbody>
        </table>
      ))}
    </div>
  );
}

function ProjectionsPage() {
  const { messageApi, entitySelected, setEntitySelected, goTo } = useContext(CriticalFinancePageContext);
  const [nextDisabled, setNextDisabled] = useState(entitySelected.years.length === 0 || entitySelected.years.length !== (parseInt(entitySelected.years_to_ipo) + 1));

  useEffect(() => {
    setNextDisabled(entitySelected.years.length === 0 || entitySelected.years.length !== (parseInt(entitySelected.years_to_ipo) + 1));
  }, [entitySelected?.years.length, entitySelected?.years_to_ipo]);

  const handleAddYear = async () => {
    messageApi.loading('Adding year...');
    try {
      const newYear = await addYearToDatabase({ entityId: entitySelected.id });
      setEntitySelected(prev => ({ ...prev, years: [...prev.years, newYear] }));
      messageApi.destroy();
      messageApi.success('Year added successfully');
    } catch (error) {
      messageApi.destroy();
      messageApi.error('Failed to add year');
    }
  };

  const saveAllYears = async () => {
    messageApi.loading('Saving all years...');
    try {
      await saveYearsInDatabase(entitySelected.years);
      messageApi.destroy();
      messageApi.success('All years saved successfully');
    } catch (error) {
      messageApi.destroy();
      messageApi.error('Failed to save years');
    }
  };

  const goNext = () => {
    if (!nextDisabled) {
      goTo(3);
    } else {
      messageApi.error('Please ensure the number of years added matches the years to IPO before proceeding and it is not zero.');
    }
  };

  return (
    <div className="pn-wrap">
      <PageHead eyebrow={entitySelected.name} title="Financial Projection"
        sub={<>Operating revenue &amp; expenses by year. <b>Years until IPO: {entitySelected.years_to_ipo}</b></>}>
        <button type="button" className="pn-btn pn-btn-ghost" onClick={() => goTo(1)}><ArrowLeftOutlined /> Back</button>
        <button type="button" className={"pn-btn pn-btn-cta" + (nextDisabled ? " " : "")} disabled={nextDisabled} onClick={goNext}>
          View Dashboard <ArrowRightOutlined />
        </button>
      </PageHead>

      <RenderYears years={entitySelected.years} setCompanySelected={setEntitySelected} messageApi={messageApi} />

      <div className="pn-addrow">
        <button type="button" className="pn-btn pn-btn-ghost" onClick={handleAddYear}><PlusOutlined /> Add year</button>
        <button type="button" className="pn-btn pn-btn-accent" onClick={saveAllYears}><SaveOutlined /> Save all years</button>
      </div>
    </div>
  );
}

export default ProjectionsPage;
